'use client';

import { useEffect, useMemo, useReducer } from 'react';

import type {
  PlinthMaterialId,
  SkyboxTheme,
} from '@/features/lobby/theming/registry';

// ---------------------------------------------------------------------------
// Path + step model (see LOBBY_PLAN §16)
// ---------------------------------------------------------------------------

export type LaunchPath = 'standard' | 'custom';

export type LaunchStepId = 'identity' | 'board' | 'review';

export interface LaunchStepMeta {
  id: LaunchStepId;
  label: string;
}

// Three steps end-to-end. Board now owns size + price + tax (the old "Launch"
// step was folded in) and yield-sources is informational only — adapters are
// attached after creation.
export const LAUNCH_STEPS: readonly LaunchStepMeta[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'board', label: 'Board' },
  { id: 'review', label: 'Review' },
] as const;

// ---------------------------------------------------------------------------
// Draft shape
// ---------------------------------------------------------------------------

export interface LaunchDraft {
  /**
   * Board path. The picker step was removed — every launch starts as
   * `'standard'`. `LaunchPath` is kept so theming/copy keyed off it
   * (`PATH_COPY[draft.path]`) keeps working without churn.
   */
  path: LaunchPath;

  name: string;
  description: string;
  /**
   * Cover image URL set in the Identity step's `IdentityCard` —
   * stored as a Pinata gateway URL after the drag-drop upload
   * resolves through `/api/ipfs/upload-image`. Empty string means
   * "no image picked yet"; the launch metadata writer simply omits
   * the field in that case.
   */
  imageUrl: string;
  /**
   * OAuth-verified X handle (without `@`). Empty string until the
   * creator clicks "Verify X" in the Identity step. The handle never
   * reaches the server unless a matching proof exists in
   * `verified_x_handles` for the caller's wallet — see the metadata
   * route's `isHandleVerified` gate.
   */
  twitterHandle: string;
  socials: { x: string; site: string; telegram: string };

  /**
   * Plinth material picked in Identity. IDs come from the theming registry
   * (`PlinthMaterialId`) so a saved draft maps directly onto a real 3D
   * material at launch time — no translation layer needed.
   */
  plinthId: PlinthMaterialId | null;
  /**
   * Skybox theme picked in Identity. IDs are `SkyboxTheme` so the picker
   * and the live scene share one vocabulary.
   */
  skyboxId: SkyboxTheme | null;

  /** n of n×n grid. Null until user picks a size. */
  sideLength: number | null;
  /** Per-seat starting price in USDC (UI uses 6-decimal stringified ints). */
  startingPriceUsdc: string;
  /**
   * Per-week Harberger tax in basis points (1% = 100 bps). Pinned to
   * the protocol default `500` (5% / wk) in `DEFAULT_DRAFT`; the
   * wizard no longer renders a slider so the field is effectively
   * read-only from the UI. Kept in the draft shape so
   * `use-launch-grid.ts` can keep passing `draft.taxRateBps` to
   * `createGrid` without churn, and so a future configurability
   * iteration can re-introduce the picker without a state migration.
   */
  taxRateBps: number;
}

export const DEFAULT_DRAFT: LaunchDraft = {
  path: 'standard',
  name: '',
  description: '',
  imageUrl: '',
  twitterHandle: '',
  socials: { x: '', site: '', telegram: '' },
  plinthId: null,
  skyboxId: null,
  sideLength: null,
  startingPriceUsdc: '1',
  taxRateBps: 500,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type LaunchAction =
  | { type: 'patch'; patch: Partial<LaunchDraft> }
  | { type: 'reset' };

function reducer(state: LaunchDraft, action: LaunchAction): LaunchDraft {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.patch };
    case 'reset':
      return DEFAULT_DRAFT;
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Step visibility
// ---------------------------------------------------------------------------

export function visibleStepsFor(_draft: LaunchDraft): LaunchStepId[] {
  return LAUNCH_STEPS.map((s) => s.id);
}

// ---------------------------------------------------------------------------
// Per-step validation
// ---------------------------------------------------------------------------

export function validateStep(
  stepId: LaunchStepId,
  draft: LaunchDraft,
): { ok: true } | { ok: false; reason: string } {
  switch (stepId) {
    case 'identity':
      if (draft.name.trim().length < 2)
        return { ok: false, reason: 'Name your board' };
      if (draft.name.length > 42)
        return { ok: false, reason: 'Name must be ≤ 42 characters' };
      if (draft.description.length > 280)
        return { ok: false, reason: 'Description must be ≤ 280 characters' };
      return { ok: true };

    case 'board': {
      if (draft.sideLength == null)
        return { ok: false, reason: 'Pick a size' };
      const seats = draft.sideLength * draft.sideLength;
      // Upper bound mirrors `GRID_CONSTRAINTS.maxSeats` (256 / 16×16)
      // — V22's single-tx lazy-init `createGrid` unblocks the full
      // contract range.
      if (seats < 4 || seats > 256)
        return { ok: false, reason: 'Pick a size' };

      const priceNum = Number(draft.startingPriceUsdc);
      if (!Number.isFinite(priceNum) || priceNum <= 0)
        return { ok: false, reason: 'Set a starting price' };

      // `taxRateBps` is no longer user-configurable — it's pinned to
      // the protocol default (5% / wk = 500 bps) in `DEFAULT_DRAFT`.
      // The slider was removed from `BoardStep` after every launch
      // we shipped converged on the default anyway and creators
      // reported the picker as noise. If a future iteration restores
      // configurability, re-add the 100–1000 bps range check here.

      return { ok: true };
    }

    case 'review':
      return { ok: true };

    default:
      return { ok: true };
  }
}

// ---------------------------------------------------------------------------
// sessionStorage persistence
//
// The wizard draft survives a same-tab full-page navigation so the
// "Verify X" redirect (mobile transport — see `useVerifyX`) doesn't
// drop the user's name/description/cover/plinth/sky picks. Scoped to
// `sessionStorage` (not `localStorage`) so a draft is per-tab and
// auto-cleans when the tab closes — a creator who half-fills a draft
// on a shared device shouldn't leak it to the next visit.
//
// Bump the version when `LaunchDraft` shape changes; mismatched
// payloads are dropped silently and the wizard falls back to defaults.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'takeover:launch-draft';
const STORAGE_VERSION = 1;

interface PersistedDraft {
  v: typeof STORAGE_VERSION;
  d: LaunchDraft;
}

function readPersistedDraft(): LaunchDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDraft;
    if (parsed?.v !== STORAGE_VERSION || typeof parsed.d !== 'object') {
      return null;
    }
    // Spread over defaults so a draft saved before a non-breaking
    // additive field landed still hydrates the new field with its
    // default value.
    const hydrated = { ...DEFAULT_DRAFT, ...parsed.d };
    // Force pin formerly-configurable fields to their current
    // protocol defaults so stale sessionStorage drafts (written when
    // the wizard still surfaced a picker for these) don't survive
    // the picker's removal:
    //   - `taxRateBps`: slider was removed; every board now ships
    //     at the 5% / wk default. Hydrating an old `700` would
    //     silently launch a 7% board with no UI affordance to
    //     change it back.
    //   - `sideLength`: anything outside the picker's currently
    //     surfaced range (2×2 was dropped after V22 launched; the
    //     ceiling is now 16×16 / 256 seats). Drop the persisted
    //     pick so the user re-selects from the currently-allowed
    //     tiles instead of landing on Review with a size that has
    //     no UI affordance to change.
    hydrated.taxRateBps = DEFAULT_DRAFT.taxRateBps;
    if (
      hydrated.sideLength != null &&
      (hydrated.sideLength < 3 || hydrated.sideLength > 16)
    ) {
      hydrated.sideLength = null;
    }
    return hydrated;
  } catch {
    return null;
  }
}

function writePersistedDraft(draft: LaunchDraft): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: PersistedDraft = { v: STORAGE_VERSION, d: draft };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private mode — non-fatal, the wizard still works.
  }
}

/**
 * Clear the persisted draft. Called by `use-launch-grid` after a
 * successful launch so the next "Create board" doesn't pre-fill with
 * the previous board's identity.
 */
export function clearPersistedLaunchDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — same as write.
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseLaunchWizard {
  draft: LaunchDraft;
  dispatch: React.Dispatch<LaunchAction>;
  steps: LaunchStepId[];
  stepIndex: (id: LaunchStepId) => number;
  nextStep: (current: LaunchStepId) => LaunchStepId | null;
  prevStep: (current: LaunchStepId) => LaunchStepId | null;
  validate: (id: LaunchStepId) => ReturnType<typeof validateStep>;
}

export function useLaunchWizard(initial?: Partial<LaunchDraft>): UseLaunchWizard {
  // Lazy initialiser — read sessionStorage exactly once on mount so
  // server-rendered defaults don't briefly flash before the persisted
  // values hydrate. `initial` (if provided) wins over storage so a
  // future deep-link "/create?prefill=…" flow can still seed the draft
  // explicitly without sessionStorage shadowing it.
  const [draft, dispatch] = useReducer(
    reducer,
    undefined,
    () => ({ ...DEFAULT_DRAFT, ...readPersistedDraft(), ...initial }),
  );

  // Autosave on every change. We don't debounce — sessionStorage writes
  // are synchronous-ish but cheap (a few hundred bytes), the wizard
  // doesn't dispatch on every keystroke (input components throttle to
  // blur in some places, but even raw on-change at typing speed is
  // well under the storage quota), and a debounce risks losing the
  // last write before a redirect.
  useEffect(() => {
    writePersistedDraft(draft);
  }, [draft]);

  return useMemo<UseLaunchWizard>(() => {
    const steps = visibleStepsFor(draft);
    const stepIndex = (id: LaunchStepId) => steps.indexOf(id);
    const nextStep = (current: LaunchStepId) => {
      const i = steps.indexOf(current);
      return i === -1 || i === steps.length - 1 ? null : steps[i + 1];
    };
    const prevStep = (current: LaunchStepId) => {
      const i = steps.indexOf(current);
      return i <= 0 ? null : steps[i - 1];
    };
    const validate = (id: LaunchStepId) => validateStep(id, draft);
    return { draft, dispatch, steps, stepIndex, nextStep, prevStep, validate };
  }, [draft]);
}
